import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import withLayout from '../../../hocs/Layout';
import { Grid, Button, Box, Chip, Typography, ToggleButton, ToggleButtonGroup, Divider, Popover, IconButton, Alert } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from "../../../common/hook-form"
import { formatDate } from '../../../helpers/UtilityHelper';
import 'react-toastify/dist/ReactToastify.css';
import DeliveryFailedOrders from "./DeliveryFailedOrders";
import DeliveredOrders from "./DeliveredOrders";
import RTOOrders from "./RTOOrders";
import AllOrders from "./AllOrders";
import InTransitOrders from "./InTransit";
import PickupOrders from "./PickupsOrders";
import { useGetNDRDataQuery } from "../../../redux/commonRTK";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MUIModal from '../../../common/MUIModal';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { RTO_BAND3_OPTIONS } from '../rtoBand';
import { subDays } from 'date-fns';
import RHFDateRangePickerField2 from '../../../common/hook-form/RHFDatePicker2';
import PageTitle from '../../../common/PageTitle';
import ForwardMisDownloadButton from './ForwardMisDownloadButton';
import { getOrderDateRangeInfo, OrderDateRangeNote } from './OrderDateRangeBanner';
import { OrderStatusWiseFilter, OrderStatusChips } from './OrderStatusFilter';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER,  SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, withOpacity } from '../../../styles/foundation/colors';
import { abdFlagFilter } from '../../appointments/shared/constants';
import {
  pageContainerSx,  filterBarSx,
   primaryBtnSx, secondaryBtnSx,
  dataGridWrapSx, btnRowSx,
} from '../../../styles/patterns';

const ListOrder2 = (props) => {
  // Deep-link seed (e.g. from the VAS dashboard: navigate('/orders', {state:{orderFilter}})).
  // Read it synchronously so the FIRST fetch is already filtered — applying it in a post-mount
  // effect would fetch once unfiltered then again filtered (a visible double-load / "reload").
  const location = useLocation();
  const seededFilter = (location.state && typeof location.state.orderFilter === 'object' && location.state.orderFilter) || null;

  const [filterChange, setFiltersChange] = useState(0);
  const [filters, setFilters] = useState(seededFilter || 0);
  const [active, setActive] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  // PTL All-Shipments in-page ABD filter (only used when the route's abdMode is 'all').
  const [abdSelection, setAbdSelection] = useState('all');
  const [tipAnchor, setTipAnchor] = useState(null);
  const merchant = getMerchant();
  const { orderListInfo } = useSelector((state) => state.orderListSlice);

  const shipmentType = props.shipmentType ? props.shipmentType : 'B2C';
  // PTL view mode is set per page via the menu (no in-page toggle): all | abd | nonabd
  const abdMode = props.abdMode || 'all';
  const startDateForLastThirtyDays = `Date('${formatDate(subDays(new Date(), 30), 'yyyy-MM-dd')} 00:00:00')`;

  let defaultAndFilters = [
    { "field": "orderSubStatus", "operator": "ne", "value": "UNPROCESSED" },
    { "field": "shipmentType", "operator": "eq", "value": shipmentType },
    { field: "awbRegisteredDate", operator: "gte", value: startDateForLastThirtyDays }
  ];
  if (props.shipmentType === 'B2C') {
    defaultAndFilters.push({ "field": "orderType", "operator": "eq", "value": 'FORWARD SHIPMENT' });
  }

  // PTL (B2B): segregate All / ABD / Non-ABD via the appointment-delivery flag.
  // Dedicated ABD/Non-ABD menu pages fix it via abdMode; the All-Shipments view ('all')
  // lets the user pick in-page via abdSelection.
  const isB2B = shipmentType === 'B2B';
  const resolvedAbdMode = abdMode === 'all' ? abdSelection : abdMode;
  const abdValue = resolvedAbdMode === 'abd' ? true : resolvedAbdMode === 'nonabd' ? false : '';
  if (isB2B && abdValue !== '') {
    // applied to the badge-count queries below
    defaultAndFilters.push(abdFlagFilter(abdValue));
  }

  const allOrderData = [];
  const { data: deliveryFailedOrders } = useGetNDRDataQuery({
    andfilter: [...defaultAndFilters, { "field": "orderStatus", "operator": "in", "value": ['FAILED_DELIVERY'] }, { "field": "merchant.$id", "operator": "eq", "value": merchant?.id }],
    "sort": { "field": "awbRegisteredDate", "direction": "DESC" },
    "offset": 0, "limit": 1, "includes": "id", countOnly: true,
  })

  const { data: pickupOrders } = useGetNDRDataQuery({
    andfilter: [...defaultAndFilters,
    { "field": "orderStatus", "operator": "in", "value": ['ORDER_PLACED', 'PICKUP_PENDING', 'OUT_FOR_PICKUP', 'PICKUP_FAILED', 'MANIFESTED', 'AWB_REGISTERED'] },
    { "field": "merchant.$id", "operator": "eq", "value": merchant?.id }],
    "sort": { "field": "awbRegisteredDate", "direction": "DESC" },
    "offset": 0, "limit": 1, "includes": "id", countOnly: true,
  })

  const { data: inTransitOrders } = useGetNDRDataQuery({
    andfilter: [...defaultAndFilters, { "field": "orderStatus", "operator": "in", "value": ['INTRANSIT', 'SHIPMENT_DELAYED', 'CONTACT_CUSTOMER_CARE', 'SHIPMENT_HELD', 'PICKED_UP', 'OUT_FOR_DELIVERY', 'REACHED_AT_DESTINATION', 'SHIPPED', 'MISSING'] }, { "field": "merchant.$id", "operator": "eq", "value": merchant?.id }],
    "sort": { "field": "awbRegisteredDate", "direction": "DESC" },
    "offset": 0, "limit": 1, "includes": "id", countOnly: true,
  })

  const { data: deliveredOrders } = useGetNDRDataQuery({
    andfilter: [...defaultAndFilters, { "field": "orderStatus", "operator": "eq", "value": 'DELIVERED' }, { "field": "merchant.$id", "operator": "eq", "value": merchant?.id }],
    "sort": { "field": "awbRegisteredDate", "direction": "DESC" },
    "offset": 0, "limit": 1, "includes": "id", countOnly: true,
  })

  const { data: rtoOrders } = useGetNDRDataQuery({
    andfilter: [...defaultAndFilters, { "field": "orderStatus", "operator": "in", "value": ['RTO_REQUESTED', 'RTO', 'RTO_INTRANSIT', 'RTO_CONTACT_CUSTOMER_CARE', 'RTO_SHIPMENT_DELAY', 'RTO_OUT_FOR_DELIVERY', 'RTO_FAILED', 'RTO_DELIVERED'] }, { "field": "merchant.$id", "operator": "eq", "value": merchant?.id }],
    "sort": { "field": "awbRegisteredDate", "direction": "DESC" },
    "offset": 0, "limit": 1, "includes": "id", countOnly: true,
  })

  useEffect(() => {
    if (deliveredOrders?.count !== undefined) {
      setDeliveredCount(deliveredOrders.count);
    }
  }, [deliveredOrders]);

  const methods = useForm({
    defaultValues: {
      state: '', city: '', awb: '', reference: '', clientOrderId: '', fromDate: '', toDate: '',
      phone: '', email: '', payment_mode: '', status: [], rtoAwb: '', rtoBand: '',
      rtoReceived: '',
    }
  });
  const { handleSubmit, reset, setValue } = methods;

  // `filters` is already seeded synchronously (above), so the list fetches once, filtered.
  // Here we only mirror the seed into the visible filter inputs so the dropdown shows it.
  useEffect(() => {
    if (seededFilter) {
      Object.entries(seededFilter).forEach(([k, v]) => setValue(k, v));
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    setValue('status', [])
    setValue('rtoReceived', '')
    // eslint-disable-next-line
  }, [active])

  // The tab-scoped fields are cleared on screen when the tab changes (above); drop them from the
  // applied filters too, else the new tab would still filter by a status the user can't see.
  const switchTab = (i) => {
    setActive(i);
    setFilters((f) => (f && (f.status?.length || f.rtoReceived) ? { ...f, status: [], rtoReceived: '' } : f));
  }

  // Re-fetch all status tabs when the ABD view mode changes (route change or in-page filter).
  useEffect(() => {
    setFiltersChange((c) => c + 1)
    // eslint-disable-next-line
  }, [abdMode, abdSelection])

  // Inject the ABD segment into the filters each tab receives (PTL only).
  const tabFilters = isB2B
    ? { ...(typeof filters === 'object' ? filters : {}), appointdeliveryRequired: abdValue }
    : filters;
  const ptlTitle = abdMode === 'nonabd' ? 'Non-ABD PTL Shipments'
    : abdMode === 'abd' ? 'ABD PTL Shipments' : 'All PTL Shipments';
  const ptlSub = abdMode === 'nonabd' ? 'PTL shipments that do not require appointment-based delivery'
    : abdMode === 'abd' ? 'PTL shipments requiring appointment-based delivery'
    : 'All PTL shipments across couriers and statuses, regardless of ABD status';
  const dateRangeInfo = getOrderDateRangeInfo(tabFilters, orderListInfo?.data);
  const titleText = isB2B ? ptlTitle : 'D2C Forward Shipments';
  const subText = isB2B ? ptlSub
    : 'Track, filter, and manage all forward shipments across couriers and statuses';

  // Plain title in the TopBar — the date range now lives only in the in-page
  // "Showing orders placed on…" note, so the top-bar title chip was removed.
  const titleNode = titleText;

  const tabs = [
    { label: 'All', icon: <ListAltIcon />, count: allOrderData?.count, color: PRIMARY_BLUE },
    { label: 'Delivery Failed', icon: <ErrorOutlineIcon />, count: deliveryFailedOrders?.count, color: ERROR },
    { label: 'Pickups', icon: <ScheduleIcon />, count: pickupOrders?.count, color: PRIMARY_BLUE },
    { label: 'In Transit', icon: <LocalShippingOutlinedIcon />, count: inTransitOrders?.count, color: '#ff9800' },
    { label: 'Delivered', icon: <CheckCircleOutlineIcon />, count: deliveredCount, color: SUCCESS },
    { label: 'RTO', icon: <ReplayIcon />, count: rtoOrders?.count, color: ERROR },
  ];

  const onSubmit = (data) => {
    setFiltersChange(filterChange + 1)
    setFilters(data);
  }

  // Plain RHF reset() only clears the form fields visually — it never touched `filters`,
  // so the table (and the applied-range banner/caption) kept showing the old query until
  // the user hit Filter again. Clear the applied filters too and bump filterChange so every
  // tab's fetch effect (which depends on props.filterChange) actually refetches.
  const onReset = () => {
    reset();
    setFilters({});
    setFiltersChange((c) => c + 1);
  }

  const updateDeliveredCount = (newCount) => {
    setDeliveredCount(newCount);
  };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle={subText}>{titleNode}</PageTitle>

      {/* Hero */}
      {/* <Box sx={pageHeroSx}>
        <Typography sx={heroTitleSx}>{titleText}</Typography>
        <Typography sx={heroSubSx}>{subText}</Typography>
      </Box> */}

      {/* Status tabs */}
      <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex' }}>
          {tabs.map((t, i) => {
            const isActive = active === i;
            const countDisplay = t.count > 99999 ? '99,999+' : t.count?.toLocaleString?.() ?? '—';
            return (
              <React.Fragment key={t.label}>
                {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
                <Box
                  onClick={() => switchTab(i)}
                  sx={{
                    flex: '1 1 auto', minWidth: 130, py: 1.25, px: 2, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                    bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: '8px',
                    bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                    color: isActive ? t.color : SUBTLE,
                    '& .MuiSvgIcon-root': { fontSize: 17 },
                  }}>
                    {t.icon}
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                      color: isActive ? NAVY : SUBTLE, lineHeight: 1.2,
                    }}>
                      {t.label}
                    </Typography>
                    <Typography sx={{
                      fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold,
                      color: isActive ? t.color : NAVY, lineHeight: 1.3,
                    }}>
                      {countDisplay}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Filter bar */}
      <Box data-rp-filters sx={filterBarSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={(e) => setTipAnchor(e.currentTarget)}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE }} />
          </IconButton>
          <Popover
            open={Boolean(tipAnchor)}
            anchorEl={tipAnchor}
            onClose={() => setTipAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { maxWidth: 360, borderRadius: '10px', overflow: 'hidden' } } }}
          >
            <Alert severity="info" sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
              <strong>Tip:</strong> Use filters below to search by AWB, reference number, client order ID, payment mode, or date range.
              With no date filter applied, your most recent orders are shown first — see the exact date range above the table.
            </Alert>
          </Popover>
        </Box>
        {isB2B && abdMode === 'all' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE }}>
              Delivery Type:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={abdSelection}
              onChange={(_e, v) => { if (v) setAbdSelection(v) }}
              sx={{
                '& .MuiToggleButton-root': {
                  fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, textTransform: 'none',
                  px: 2, py: 0.5, borderColor: BORDER, color: NAVY,
                  '&.Mui-selected': { bgcolor: PRIMARY_BLUE, color: WHITE, '&:hover': { bgcolor: PRIMARY_BLUE } },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="abd">ABD</ToggleButton>
              <ToggleButton value="nonabd">Non-ABD</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <RHFTextField name="reference" label="Order Number" color="secondary" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <RHFTextField name="clientOrderId" label="Client Order ID" color="secondary" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <RHFTextField name="awb" label="AWB Number(s) — comma separated"  color="secondary" size="small" />
            </Grid>
            {[0, 5].includes(active) && (
              <Grid item xs={12} sm={6} md={2}>
                <RHFTextField name="rtoAwb" label="RTO AWB Number" color="secondary" size="small" />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <RHFSelectField label="Payment Mode" name="payment_mode" options={[{ label: "COD", value: 'COD' }, { label: "PREPAID", value: 'PREPAID' }]} width="100%" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <RHFSelectField label="RTO Risk" name="rtoBand" options={RTO_BAND3_OPTIONS} width="100%" enableNoneSelect />
            </Grid>
            {[0, 2, 3, 5].includes(active) && (
              <Grid item xs={12} sm={6} md={3}>
                <OrderStatusWiseFilter active={active} />
              </Grid>
            )}
            {active === 5 && (
              <Grid item xs={12} sm={6} md={2}>
                <RHFSelectField
                  label="RTO Received Marking"
                  name="rtoReceived"
                  options={[
                    { label: "Marked as Received", value: 'MARKED' },
                    { label: "Not Marked", value: 'NOT_MARKED' }
                  ]}
                  width="100%"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <RHFDateRangePickerField label="Created Date" maxDate={new Date()} />
            </Grid>
            {[0].includes(active) && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <RHFTextField name="email" label="Customer Email" color="secondary" size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <RHFTextField name="phone" label="Customer Phone" color="secondary" size="small" />
                </Grid>
              </>
            )}
            {[4].includes(active) && (
              <Grid item xs={12} sm={6} md={3}>
                <RHFDateRangePickerField2 label="Delivered Date" maxDate={new Date()} fromDateName="deliveryFromDate" toDateName="deliveryToDate" />
              </Grid>
            )}
            {[2].includes(active) && (
              <Grid item xs={12} sm={6} md={3}>
                <RHFDateRangePickerField2 label="Pickup Date" maxDate={new Date()} fromDateName="pickUpFromDate" toDateName="pickUpToDate" />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={isB2B ? 3 : 4}>
              <Box sx={btnRowSx}>
                <Button type="submit" variant="contained" sx={primaryBtnSx} size="small">Filter</Button>
                <Button variant="outlined" sx={secondaryBtnSx} size="small" onClick={onReset}>Reset</Button>
                {/* D2C forward shipments only. Uses the exact range the page says it is showing: the applied
                    Created Date, or - with none applied - the dates of the most recent orders it loaded. */}
                {!isB2B && (
                  <ForwardMisDownloadButton
                    merchantId={merchant?.id}
                    fromDate={dateRangeInfo.fromDate}
                    toDate={dateRangeInfo.toDate}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          <OrderStatusChips applied={filters?.status} onApply={onSubmit} />
          {filters?.eddBreached && (
            <Chip size='small' color='primary' sx={{ mt: 1.5, '& .MuiChip-label': { textTransform: 'none' } }}
              label='EDD breached: past EDD, not delivered yet'
              onDelete={() => { setFilters((f) => ({ ...f, eddBreached: '' })); setFiltersChange((c) => c + 1); }} />
          )}
          {filters?.eddDate && (
            <Chip size='small' color='primary' sx={{ mt: 1.5, '& .MuiChip-label': { textTransform: 'none' } }}
              label={`EDD ${formatDate(new Date(`${filters.eddDate}T00:00:00`), 'dd MMM yyyy')} (scheduled today)`}
              onDelete={() => { setFilters((f) => ({ ...f, eddDate: '' })); setFiltersChange((c) => c + 1); }} />
          )}
          {dateRangeInfo.hasCustomRange && (
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: PRIMARY_BLUE, mt: 1.5 }}>
              Applied: {dateRangeInfo.from} – {dateRangeInfo.to}
            </Typography>
          )}
        </FormProvider>
      </Box>

      {/* Tab content */}
      <OrderDateRangeNote dateRangeInfo={dateRangeInfo} />
      <Box sx={dataGridWrapSx}>
        {active === 0 && <AllOrders shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={allOrderData?.count} merchantId={merchant?.id} />}
        {active === 1 && <DeliveryFailedOrders shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={deliveryFailedOrders?.count} merchantId={merchant?.id} />}
        {active === 2 && <PickupOrders shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={pickupOrders?.count} merchantId={merchant?.id} />}
        {active === 3 && <InTransitOrders shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={inTransitOrders?.count} merchantId={merchant?.id} />}
        {active === 4 && <DeliveredOrders updateDeliveredCount={updateDeliveredCount} shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={deliveredOrders?.count} merchantId={merchant?.id} />}
        {active === 5 && <RTOOrders shipmentType={shipmentType} filters={tabFilters} filterChange={filterChange} tab={active} count={rtoOrders?.count} merchantId={merchant?.id} />}
      </Box>
    </Box>
  );
};

export const exclude = "returnInfo,itemList.hsn,deliveryTime,itemList.itemUrl,itemList.itemDescription,actualDeliveryTat,proshipDeliveryTat,courierDeliveryTat,lastStatusUpdateTime,epd,lifeCycleComplete,courier,merchantDetail,courierId,version,order_history,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customer_detail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricingRTO,modifiedByUser,lastModifiedDate,merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj";

export function ItemListModal(props) {
  const { data, itemModal, setItemModal } = props;
  const columnsItemModal = [
    { field: "sku_id", headerAlign: "left", hideSortIcons: true, headerName: "SKU", flex: 1 },
    { field: "item_name", headerAlign: "left", hideSortIcons: true, headerName: "SKU Name", flex: 2 },
    { field: "units", headerAlign: "left", hideSortIcons: true, headerName: "Units", flex: 0.6 },
    { field: "selling_price", headerAlign: "left", hideSortIcons: true, headerName: "Selling Price", flex: 1 },
    { field: "tax", headerAlign: "left", hideSortIcons: true, headerName: "Tax", flex: 1 },
  ];
  return (
    <MUIModal open={itemModal} title="SKU List" handleClose={() => setItemModal(false)} maxWidth="sm">
      <ProzoDataGrid
        autoHeight={true}
        columns={columnsItemModal}
        rows={data?.length > 0 ? data?.map((val, index) => ({ ...val, id: index })) : []}
        getRowId={(row) => row?.id}
        paginationMode="client"
        rowCount={data?.length}
      />
    </MUIModal>
  )
}

export default withLayout(ListOrder2);
